/**
 * WarehousePage — 창고 마스터 관리 (W19-C2, SC1 ListCrud 패턴).
 *
 * 【역할】
 * - tb_warehouse 마스터 CRUD — warehouseCode (UNIQUE), warehouseName, location, isActive.
 * - 재고 이동/실사 페이지에서 참조하는 상위 마스터.
 *
 * 【주요 기능】
 * - 조회: keyword + isActive 필터 (warehouse/searchList)
 * - 추가 (Modal Form) / 편집 (인라인) / 삭제 (D state) / 저장 (warehouse/save)
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useLabel (다국어 라벨)
 * - useI18nTickStore (locale 전환 시 강제 re-render)
 *
 * 【Backend RPC serviceName】
 * - warehouse/searchList (search)
 * - warehouse/save (save)
 *
 * 【DataSet 구조】
 * - ds_warehouse: (PK warehouseId, 변경추적 C/U/D)
 *
 * 【사용처】 router/index.tsx — `{ path: 'warehouse' }`
 * 【포팅 원본】 (없음 — Wave 19 신설)
 */
import { useEffect, useState } from 'react'
import { Alert, Form, Modal, Input, Select, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useLabel } from '@/framework/useLabel'
import { useI18nTickStore } from '@/framework/useLocale'
import RowStateTag from '@/components/common/RowStateTag'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'

interface WarehouseRow {
  warehouseId?: number
  warehouseCode?: string
  warehouseName?: string
  location?: string
  isActive?: number
}

/**
 * WarehousePage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function WarehousePage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)

  const ds_warehouse = $createDataSet<WarehouseRow>([])
  const [searchForm] = Form.useForm()
  const [editOpen, setEditOpen] = useState(false)
  const [editForm] = Form.useForm<WarehouseRow>()
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)

  const fn_search = async () => {
    setLoading(true)
    const res = await $transaction({
      id: 'wh-search',
      service: 'warehouse/searchList',
      action: 'search',
      inds: { ds_search: searchForm.getFieldsValue() },
      outds: { ds_warehouse }
    })
    setLoading(false)
    if (res.success) setSelectedKeys([])
  }

  const fn_save = async () => {
    if (!ds_warehouse.isDirty) return $msg.byId('MSG_NO_CHANGES')
    const res = await $transaction({
      id: 'wh-save',
      service: 'warehouse/save',
      action: 'save',
      inds: { ds_warehouse }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search()
    }
  }

  const fn_add = () => {
    editForm.resetFields()
    editForm.setFieldsValue({ isActive: 1 })
    setEditOpen(true)
  }

  const fn_editSubmit = async () => {
    const v = await editForm.validateFields()
    ds_warehouse.addRow(v)
    setEditOpen(false)
  }

  const fn_delete = () => {
    if (!selectedKeys.length) return $msg.byId('MSG_SELECT_DELETE_ROW')
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      for (const key of selectedKeys) {
        const idx = ds_warehouse.rows.findIndex((r) => r._rowId === key)
        if (idx >= 0) ds_warehouse.deleteRow(idx)
      }
      setSelectedKeys([])
    })
  }

  useEffect(() => {
    fn_search()
  }, [])

  const visibleRows = ds_warehouse.getVisibleRows()
  const columns: ColumnsType<(typeof visibleRows)[number]> = [
    {
      title: t('COL_WAREHOUSE_CODE', '창고코드'),
      dataIndex: ['data', 'warehouseCode'],
      width: 140
    },
    { title: t('COL_WAREHOUSE_NAME', '창고명'), dataIndex: ['data', 'warehouseName'], width: 200 },
    { title: t('COL_LOCATION', '위치'), dataIndex: ['data', 'location'] },
    {
      title: t('COL_IS_ACTIVE', '사용여부'),
      dataIndex: ['data', 'isActive'],
      width: 100,
      render: (v) =>
        v === 1 ? (
          <Tag color="green">{t('LBL_YES', '사용')}</Tag>
        ) : (
          <Tag>{t('LBL_NO', '미사용')}</Tag>
        )
    },
    {
      title: t('COL_STATUS', '상태'),
      dataIndex: '_state',
      width: 80,
      render: (s: string) => <RowStateTag state={s} />
    }
  ]

  return (
    <>
      <Alert
        type="info"
        showIcon
        message={t('MSG_WAREHOUSE_MASTER_INFO', '창고 마스터 — 재고이동/실사에서 참조됩니다.')}
        style={{ marginBottom: 12 }}
      />
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          loading,
          content: (
            <>
              <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
                <FormInput
                  placeholder={t('LBL_WAREHOUSE_CODE_NAME', '창고코드/명')}
                  style={{ width: 200 }}
                />
              </Form.Item>
              <Form.Item name="isActive" label={t('LBL_IS_ACTIVE', '사용여부')}>
                <Select
                  allowClear
                  style={{ width: 120 }}
                  options={[
                    { label: t('LBL_YES', '사용'), value: 1 },
                    { label: t('LBL_NO', '미사용'), value: 0 }
                  ]}
                />
              </Form.Item>
            </>
          )
        }}
        toolbar={{
          onSearch: fn_search,
          onAdd: fn_add,
          onDelete: fn_delete,
          onSave: fn_save,
          onCancel: fn_search,
          loading,
          dirty: ds_warehouse.isDirty
        }}
      >
        <DataTable
          rowKey={(r) => r._rowId}
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys)
          }}
        />
      </AppPage>

      <Modal
        open={editOpen}
        title={t('TTL_ADD_WAREHOUSE', '창고 추가')}
        onOk={fn_editSubmit}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="warehouseCode"
            label={t('LBL_WAREHOUSE_CODE', '창고코드')}
            rules={[{ required: true }]}
          >
            <Input placeholder="MAIN / EAST …" />
          </Form.Item>
          <Form.Item
            name="warehouseName"
            label={t('LBL_WAREHOUSE_NAME', '창고명')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="location" label={t('LBL_LOCATION', '위치')}>
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label={t('LBL_IS_ACTIVE', '사용여부')}>
            <Select
              options={[
                { label: t('LBL_YES', '사용'), value: 1 },
                { label: t('LBL_NO', '미사용'), value: 0 }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
